import React from 'react';

import cx from 'classnames';
import { notification as NotificationCore } from 'antd';

import {
	TickWithBorderIcon,
	InfoIcon,
	CrossWithBorderIcon,
	CrossIcon,
	ExclamationIcon,
} from '@/assets/icons';

import styles from './Notification.module.scss';

/**
 * @short_description UI Component to display the notification messages globally. Underlying it is using the antd notification component.
 *
 * @author  Hemant Kumar Goyal <@hemantkumar.goyal>
 *
 * @reference https://ant.design/components/notification
 *
 * @description Since the notification extracts some top level API's which can be used directly in the component to invoke the notification from
 * anywhere in the app since we have assigned the notification instance directly to our internal notification component.
 *
 * @returns {instanceof notification}
 */

const defautConfig = {
	maxCount: 3,
	closeIcon: <CrossIcon className={cx(styles.closeIconStyle)} />,
};

const allowedTypes = (type, variant) => {
	const notificationType = {
		success: {
			className: cx(styles.notificationStyle, styles[`success-${variant}`]),
			icon: <TickWithBorderIcon className={styles.iconStyle} />,
			type: 'success',
		},
		error: {
			className: cx(styles.notificationStyle, styles[`error-${variant}`]),
			icon: <CrossWithBorderIcon className={styles.iconStyle} />,
			type: 'error',
		},
		warning: {
			className: cx(styles.notificationStyle, styles[`warning-${variant}`]),
			icon: <ExclamationIcon className={styles.iconStyle} />,
			type: 'warning',
		},
		info: {
			className: cx(styles.notificationStyle, styles[`info-${variant}`]),
			icon: <InfoIcon className={styles.iconStyle} />,
			type: 'info',
		},
		textOnly: {
			className: cx(styles.notificationStyle, styles.textOnly),
			icon: null,
			type: 'open',
		},
	};

	return notificationType[type] || notificationType.info;
};

const Notification = {
	...NotificationCore,
	show: ({ type = 'info', variant = 'light', config = {}, ...restProps }) => {
		const notificationType = allowedTypes(type, variant);
		NotificationCore.config({
			...defautConfig,
			...config,
		});
		NotificationCore[notificationType.type]({
			className: notificationType.className,
			icon: notificationType.icon,
			...restProps,
		});
	},
};

export default Notification;
